import { useCallback, useEffect, useState } from 'react'
import { getPushPublicKey, subscribeToPush, unsubscribeFromPush } from '../services/pushNotificationsApi'

// Standard VAPID-key conversion (browsers require the applicationServerKey as
// a raw Uint8Array, not the base64url string the server hands back).
function urlBase64ToUint8Array(base64Url: string): Uint8Array {
  const padding = '='.repeat((4 - (base64Url.length % 4)) % 4)
  const base64 = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  const output = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i)
  return output
}

type PermissionState = 'unsupported' | 'default' | 'granted' | 'denied'

export function usePushNotifications() {
  const [permission, setPermission] = useState<PermissionState>('default')
  const [subscribed, setSubscribed] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const supported = typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window

  useEffect(() => {
    let cancelled = false
    if (!supported) {
      setLoading(false)
      setPermission('unsupported')
      return
    }

    setPermission(Notification.permission as PermissionState)

    navigator.serviceWorker.register('/sw.js')
      .then((registration) => registration.pushManager.getSubscription())
      .then((existing) => { if (!cancelled) setSubscribed(!!existing) })
      .catch(() => { /* registration failing just leaves subscribed=false */ })
      .finally(() => { if (!cancelled) setLoading(false) })

    return () => { cancelled = true }
  }, [supported])

  const enable = useCallback(async () => {
    if (!supported) return
    setBusy(true)
    setError(null)
    try {
      const permissionResult = await Notification.requestPermission()
      setPermission(permissionResult as PermissionState)
      if (permissionResult !== 'granted') {
        // Denied or dismissed — nothing to subscribe. A prior "denied" can
        // only be changed from the browser's own site-settings UI; we can't
        // re-prompt, so the caller's UI should say that rather than retry.
        return
      }

      const { data } = await getPushPublicKey()
      if (!data.configured || !data.publicKey) {
        setError('Push notifications are not configured on the server yet.')
        return
      }

      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey),
      })

      const json = subscription.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } }
      if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
        throw new Error('Browser returned an incomplete push subscription')
      }

      await subscribeToPush({
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
        userAgent: navigator.userAgent,
      })
      setSubscribed(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to enable push notifications')
    } finally {
      setBusy(false)
    }
  }, [supported])

  const disable = useCallback(async () => {
    if (!supported) return
    setBusy(true)
    setError(null)
    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      if (subscription) {
        await unsubscribeFromPush(subscription.endpoint).catch(() => {})
        await subscription.unsubscribe()
      }
      setSubscribed(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disable push notifications')
    } finally {
      setBusy(false)
    }
  }, [supported])

  return { supported, permission, subscribed, loading, busy, error, enable, disable }
}
